import { useCallback, useEffect, useState } from 'react';

import { ChevronLeft } from '../icons.js';
import { THEME_LABELS, readTheme, setTheme } from '../theme.js';
import { describePasskeyRegistrationFailure } from '../auth/errors.js';

import {
  AVAILABLE_SPRINT_TYPES,
  AVAILABLE_THEMES,
  SPRINT_TYPE_SOON,
  THEME_SOON,
  availableTheme,
} from './availability.js';
import {
  SCOPE_HINTS,
  SCOPE_LABELS,
  callLabel,
  deviceLabel,
  passkeyHostLabels,
  passkeyLabel,
  tokenLabel,
} from './settings.js';

import type { Theme } from '../theme.js';
import type {
  ApiCallSummary,
  ApiTokenSummary,
  DeviceSummary,
  PasskeySummary,
  Session,
} from '../../core/auth/session.js';
import type { ApiScope, SprintType } from '@nedelnik/shared';

/**
 * Настройки: длина спринта, тема, ключи, устройства и безопасность.
 *
 * До этого экрана всё перечисленное жило в подвале рядом с индикатором
 * синхронизации — временно и вперемешку с учётной записью. Настройки нужны
 * изредка, поэтому вызываются, а не висят на основном экране (принцип 2).
 *
 * Разделы идут по частоте, а не по важности: длину спринта и тему меняют
 * иногда, ключи и устройства — редко, безопасность читают один раз.
 */
export function SettingsView({
  session,
  email,
  sprintType,
  onSprintType,
  onSignedOut,
  onFlushBeforeSignOut,
  onBack,
  backLabel,
}: {
  session: Pick<
    Session,
    | 'listPasskeys'
    | 'registerPasskey'
    | 'deletePasskey'
    | 'listDevices'
    | 'revokeDevice'
    | 'listTokens'
    | 'createToken'
    | 'revokeToken'
    | 'listTokenCalls'
    | 'deleteAccount'
  >;
  email: string | undefined;
  sprintType: SprintType;
  onSprintType: (type: SprintType) => void;
  /** Входа больше нет: вышли здесь же или удалили учётную запись. */
  onSignedOut: () => void;
  /**
   * Дослать очередь перед выходом и сказать, сколько правок уехать не смогло.
   *
   * Выход стирает локальную копию вместе с очередью, а сессию на сервере к
   * тому моменту уже погасили — досылать станет и некуда, и нечем. Поэтому
   * спрашивают до отзыва, а не после.
   */
  onFlushBeforeSignOut: () => Promise<number>;
  onBack: () => void;
  backLabel: string;
}): React.ReactElement {
  return (
    <section className="screen">
      <header className="screen__head">
        {/*
          Имя начинается с «Назад», а видимая подпись входит в него целиком:
          иначе в озвучке это просто «Сентябрь, кнопка» — слово, которое ничего
          не обещает. Требование «видимый текст — часть имени» при этом
          соблюдено.
        */}
        <button type="button" className="link" aria-label={`Назад: ${backLabel}`} onClick={onBack}>
          <ChevronLeft />
          {backLabel}
        </button>
        <h1>Настройки</h1>
        <p className="head__sub">{email ?? 'учётная запись'}</p>
      </header>

      <SprintLength value={sprintType} onChange={onSprintType} />
      <ThemeSection />
      <PasskeySection session={session} />
      <DeviceSection
        session={session}
        onSignedOut={onSignedOut}
        onFlushBeforeSignOut={onFlushBeforeSignOut}
      />
      <AgentSection session={session} />
      <SecuritySection session={session} email={email} onDeleted={onSignedOut} />
    </section>
  );
}

const ALL_SCOPES: readonly ApiScope[] = ['read', 'write', 'destroy'];

/** Адрес моста — с того же домена, откуда открыто приложение: стенд и прод различаются им. */
function connectorUrl(): string {
  const origin = globalThis.location?.origin;
  return `${origin !== undefined && origin.length > 0 ? origin : 'https://onlap.app'}/mcp`;
}

/**
 * Токены агента (3.16.7, этап 14 плана).
 *
 * Раздел стоит после устройств, потому что отвечает на тот же вопрос — «кто
 * имеет доступ к моим задачам», — но про не-людей. Токен показывается один
 * раз, при выпуске, и это не строгость ради строгости: в базе его нет,
 * показать второй раз нечего. Поэтому рядом с ним кнопка «Скопировать» и
 * прямое предупреждение.
 *
 * Скоупы выбираются галочками, а не одним переключателем «полный доступ»:
 * агенту, который только записывает продиктованное, «удалять» не нужно, и
 * лишнее право — это лишний ущерб от утёкшего токена. По умолчанию — читать
 * и писать: ровно то, что нужно для «записал — появилось» и «разбери
 * Входящие».
 */
function AgentSection({
  session,
}: {
  session: Pick<Session, 'listTokens' | 'createToken' | 'revokeToken' | 'listTokenCalls'>;
}): React.ReactElement {
  const [tokens, setTokens] = useState<ApiTokenSummary[] | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [showGone, setShowGone] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>(['read', 'write']);
  const [revealed, setRevealed] = useState<{ name: string; token: string } | undefined>(undefined);
  const [calls, setCalls] = useState<{ id: string; list: ApiCallSummary[] } | undefined>(undefined);

  const refresh = useCallback(() => {
    session.listTokens().then(setTokens, () => setTokens(undefined));
  }, [session]);

  useEffect(refresh, [refresh]);

  const issue = useCallback(async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setNotice('Дайте токену имя — по нему вы его потом отзовёте');
      return;
    }
    if (scopes.length === 0) {
      setNotice('Выберите хотя бы одно право');
      return;
    }
    try {
      const created = await session.createToken(trimmed, scopes);
      setRevealed({ name: created.name, token: created.token });
      setName('');
      setNotice(undefined);
      refresh();
    } catch {
      setNotice('Выпустить токен не вышло — попробуйте ещё раз');
    }
  }, [name, scopes, session, refresh]);

  const active = tokens?.filter((token) => token.revoked_at == null) ?? [];
  const gone = tokens?.filter((token) => token.revoked_at != null) ?? [];
  const shown = showGone ? [...active, ...gone] : active;

  return (
    <section className="set">
      <h2 className="set__title">Агент</h2>
      <p className="set__hint">
        Токен пускает в ваши задачи не человека, а помощника — Claude на компьютере или в телефоне.
        Он ходит от вашего имени и только в вашу учётную запись.
      </p>

      {tokens === undefined ? (
        <p className="set__hint">Список токенов не пришёл — сервер не ответил.</p>
      ) : shown.length === 0 ? (
        <p className="set__hint">Токенов нет: агент пока не подключён.</p>
      ) : (
        <ul className="links__list" aria-label="Токены агента">
          {shown.map((token) => (
            <li key={token.id} className="links__item links__item--token">
              <span className="set__row">
                {tokenLabel(token)}
                <span className="set__sub">
                  {token.scopes.map((scope) => SCOPE_LABELS[scope]).join(', ')}
                </span>
              </span>
              <div className="set__row-actions">
                <button
                  type="button"
                  className="link"
                  aria-label={`Показать вызовы токена ${token.name}`}
                  onClick={() => {
                    if (calls?.id === token.id) {
                      setCalls(undefined);
                      return;
                    }
                    session.listTokenCalls(token.id).then(
                      (list) => setCalls({ id: token.id, list }),
                      () => setNotice('Вызовы не пришли — сервер не ответил'),
                    );
                  }}
                >
                  {calls?.id === token.id ? 'Скрыть вызовы' : 'Вызовы'}
                </button>
                {token.revoked_at == null && (
                  <button
                    type="button"
                    className="danger"
                    aria-label={`Отозвать токен: ${token.name}`}
                    onClick={() => {
                      session.revokeToken(token.id).then(
                        () => {
                          setNotice('Токен отозван: агент больше не войдёт');
                          refresh();
                        },
                        () => setNotice('Отозвать не вышло — попробуйте ещё раз'),
                      );
                    }}
                  >
                    Отозвать
                  </button>
                )}
              </div>
              {calls?.id === token.id && (
                <ul className="set__calls" aria-label={`Вызовы токена ${token.name}`}>
                  {calls.list.length === 0 ? (
                    <li>Вызовов ещё не было.</li>
                  ) : (
                    calls.list.map((call, index) => (
                      <li key={`${call.at}-${String(index)}`}>{callLabel(call)}</li>
                    ))
                  )}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {gone.length > 0 && (
        <button type="button" className="link" onClick={() => setShowGone(!showGone)}>
          {showGone ? 'Скрыть отозванные' : `Показать отозванные (${String(gone.length)})`}
        </button>
      )}

      {revealed !== undefined && (
        <div className="token" role="status">
          <p className="set__hint">
            Токен «{revealed.name}» — скопируйте сейчас: второй раз он не показывается, в базе
            хранится только его отпечаток.
          </p>
          <code className="token__value">{revealed.token}</code>
          <div className="set__actions">
            <button
              type="button"
              onClick={() => {
                const clipboard = navigator.clipboard as Clipboard | undefined;
                if (clipboard === undefined) {
                  setNotice('Буфер обмена недоступен — выделите токен и скопируйте руками');
                  return;
                }
                clipboard.writeText(revealed.token).then(
                  () => setNotice('Токен скопирован'),
                  () => setNotice('Скопировать не вышло — выделите токен и скопируйте руками'),
                );
              }}
            >
              Скопировать
            </button>
            <button type="button" className="link" onClick={() => setRevealed(undefined)}>
              Спрятать
            </button>
          </div>
        </div>
      )}

      <form
        className="set__form"
        onSubmit={(event) => {
          event.preventDefault();
          void issue();
        }}
      >
        <label className="field">
          <span className="field__label">Имя токена</span>
          <input
            type="text"
            value={name}
            maxLength={64}
            placeholder="Claude на ноутбуке"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <fieldset className="set__choices">
          <legend className="field__label">Что разрешено</legend>
          {ALL_SCOPES.map((scope) => (
            <label key={scope} className="set__choice">
              <input
                type="checkbox"
                checked={scopes.includes(scope)}
                onChange={(event) => {
                  setScopes(
                    event.target.checked
                      ? ALL_SCOPES.filter((item) => item === scope || scopes.includes(item))
                      : scopes.filter((item) => item !== scope),
                  );
                }}
              />
              <span>
                {SCOPE_LABELS[scope]}
                <span className="set__sub">{SCOPE_HINTS[scope]}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <button type="submit">Выпустить токен</button>
      </form>

      <Details summary="Как подключить агента">
        С телефона и на claude.ai: в настройках Claude добавьте коннектор с адресом{' '}
        <code>{connectorUrl()}</code> — он сам приведёт сюда за разрешением, а токен появится в этом
        списке под именем клиента. В Claude Code на компьютере: выпустите токен здесь и подключите
        локальный сервер, как описано в инструкции пакета mcp. Всё, что агент сделает, видно по
        кнопке «Вызовы»: когда, что и с какими задачами. Отзыв действует сразу и не выкидывает вас
        самих.
      </Details>

      {notice !== undefined && (
        <p className="set__hint" role="status">
          {notice}
        </p>
      )}
    </section>
  );
}

/**
 * Длина спринта — единственная настройка, которая синхронизируется.
 *
 * Она про сами задачи: спринт, разный на телефоне и ноутбуке, показывал бы
 * два разных набора одних и тех же задач. Смена ключа периода при этом
 * безопасна — спринт не хранится, а считается из даты (3.2.1).
 */
function SprintLength({
  value,
  onChange,
}: {
  value: SprintType;
  onChange: (type: SprintType) => void;
}): React.ReactElement {
  const [soon, setSoon] = useState(false);

  function pick(type: SprintType): void {
    // Закрытый вариант не шлёт правку: иначе «месяц» уехал бы на остальные
    // устройства, хотя ни одно из них его не покажет.
    setSoon(!AVAILABLE_SPRINT_TYPES.includes(type));
    if (AVAILABLE_SPRINT_TYPES.includes(type)) onChange(type);
  }

  return (
    <section className="set">
      <h2 className="set__title">Длина спринта</h2>
      <div className="seg" role="group" aria-label="Длина спринта">
        <button type="button" aria-pressed={value === 'week'} onClick={() => pick('week')}>
          Неделя
        </button>
        <button type="button" aria-pressed={value === 'month'} onClick={() => pick('month')}>
          Месяц
        </button>
      </div>
      <p className="set__hint">Общая для всех устройств.</p>
      {soon && (
        <p className="set__hint" role="status">
          {SPRINT_TYPE_SOON}
        </p>
      )}
    </section>
  );
}

/**
 * Длинное объяснение, свёрнутое до вопроса.
 *
 * Настройки читают один раз, а открывают часто: три абзаца под каждым
 * разделом превращали экран в простыню, по которой ищут переключатель. Но и
 * выбросить их нельзя — там сказано то, чего продукт не обещает, и умолчать
 * значит соврать.
 *
 * `details` браузерный, а не свой: он открывается без скрипта, ищется поиском
 * по странице и правильно читается озвучкой.
 */
function Details({
  summary,
  children,
}: {
  summary: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <details className="set__more">
      <summary>{summary}</summary>
      <p className="set__hint">{children}</p>
    </details>
  );
}

const THEMES: readonly Theme[] = ['light', 'dark', 'system'];

/** Тема, наоборот, живёт в устройстве: она про экран, а не про задачи. */
function ThemeSection(): React.ReactElement {
  const [theme, setCurrent] = useState<Theme>(() => availableTheme(readTheme()));
  const [soon, setSoon] = useState(false);

  return (
    <section className="set">
      <h2 className="set__title">Тема</h2>
      <div className="seg" role="group" aria-label="Тема">
        {THEMES.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={theme === value}
            onClick={() => {
              setSoon(!AVAILABLE_THEMES.includes(value));
              if (!AVAILABLE_THEMES.includes(value)) return;
              setTheme(value);
              setCurrent(value);
            }}
          >
            {THEME_LABELS[value]}
          </button>
        ))}
      </div>
      <p className="set__hint">Только на этом устройстве.</p>
      {soon && (
        <p className="set__hint" role="status">
          {THEME_SOON}
        </p>
      )}
    </section>
  );
}

/**
 * Ключи входа.
 *
 * Список — про учётную запись, а не про это устройство, и это надо говорить
 * вслух. Раздел стоит рядом с «Устройствами», где строки как раз про
 * устройства, и молча читается так же; отсюда законный вопрос «что тогда
 * значит „Снять“, если про этот телефон вы всё равно не знаете».
 *
 * Знать и не нужно: «Снять» — это про сервер. Он перестаёт принимать ключ, кем
 * бы тот ни предъявлялся. Сам ключ остаётся лежать в связке ключей устройства,
 * стереть его оттуда мы не можем — это ровно та граница, которую WebAuthn
 * держит намеренно.
 *
 * Снять последний ключ сервер не даст, пока почта не подтверждена (3.16.6), и
 * объяснить это должен он же: правило живёт там, где данные, а второй его
 * экземпляр здесь разъехался бы с первым.
 */
function PasskeySection({
  session,
}: {
  session: Pick<Session, 'listPasskeys' | 'registerPasskey' | 'deletePasskey'>;
}): React.ReactElement {
  const [passkeys, setPasskeys] = useState<PasskeySummary[] | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const refresh = useCallback(() => {
    session.listPasskeys().then(setPasskeys, () => setPasskeys(undefined));
  }, [session]);

  useEffect(refresh, [refresh]);

  const here = globalThis.location?.hostname ?? '';
  const hosts = passkeyHostLabels(passkeys ?? [], here);
  const foreign = passkeys?.some((passkey) => passkey.rp_id !== here) ?? false;

  return (
    <section className="set">
      <h2 className="set__title">Вход без почты</h2>

      {passkeys === undefined ? (
        <p className="set__hint">Список ключей не пришёл — сервер не ответил.</p>
      ) : passkeys.length === 0 ? (
        <p className="set__hint">
          Ключей нет: вход только по ссылке на почту. Привязанный ключ пускает отпечатком или лицом,
          без письма.
        </p>
      ) : (
        <>
          <p className="set__hint">Ключи всей учётной записи, со всех устройств.</p>
          <ul className="links__list" aria-label="Ключи входа">
            {passkeys.map((passkey) => (
              <li key={passkey.id} className="links__item">
                <span className="set__row">{passkeyLabel(passkey, hosts.get(passkey.id))}</span>
                <button
                  type="button"
                  className="danger"
                  aria-label={`Отозвать ключ везде: ${passkeyLabel(passkey, hosts.get(passkey.id))}`}
                  onClick={() => {
                    session.deletePasskey(passkey.id).then(
                      () => {
                        setNotice('Ключ отозван: сервер больше его не примет');
                        refresh();
                      },
                      (error: unknown) => setNotice(describeKeyFailure(error)),
                    );
                  }}
                >
                  {/*
                    «Снять» звучало как «убрать отсюда» — то есть как действие
                    над устройством, у которого человек стоит. На деле это
                    действие над сервером: он перестаёт принимать ключ, откуда
                    бы тот ни предъявлялся. «Везде» — самое короткое слово,
                    которое об этом говорит.
                  */}
                  Отозвать везде
                </button>
              </li>
            ))}
          </ul>
          {foreign && (
            // Без объяснения ключ в списке, который не предлагается при входе,
            // выглядит поломкой. Отзывать его не обязательно: он мешает только
            // тем, что занимает строку.
            <p className="set__hint">
              Ключ пускает только на том адресе, где его завели: здесь, на {here}, ключи других
              адресов не сработают. Привяжите новый кнопкой ниже, а старый отзовите, когда
              перестанете открывать прежний адрес.
            </p>
          )}
          <Details summary="Что значит «отозвать везде»">
            Сервер перестанет принимать этот ключ, с какого бы устройства его ни предъявили. Сам
            ключ останется лежать в связке ключей телефона или в браузере: стереть его оттуда мы не
            можем — это делается там же, где он хранится. Какой ключ где, приложение не знает и
            знать не может: браузер не отвечает на вопрос «есть ли у вас ключ от этого сайта», иначе
            страница входа стала бы способом это выяснять.
          </Details>
        </>
      )}

      <button
        type="button"
        onClick={() => {
          setNotice('Привязываем ключ…');
          // Имени не передаём: его ставит сервер по `User-Agent`. Подпись
          // «Этот браузер», которую слал клиент, читалась с другого устройства
          // как «ключ здесь», хотя ключ был заведён совсем не здесь.
          void session.registerPasskey().then(
            () => {
              setNotice('Ключ привязан');
              refresh();
            },
            // Объяснение берётся оттуда же, откуда его берёт экран входа:
            // голый `error.message` — это английский текст WebKit ровно в тот
            // момент, когда человеку нужно понять, что делать дальше.
            (error: unknown) => setNotice(describePasskeyRegistrationFailure(error)),
          );
        }}
      >
        Привязать ключ на этом устройстве
      </button>

      {/*
        Подсказка стоит всегда, а не только у пустого списка.
        Привязанный ключ делает кнопку бессмысленной на вид — «зачем второй,
        если один уже есть», — и ответ на это должен быть на экране: ключ живёт
        в устройстве, а не в учётной записи, и на новом телефоне его нет.
        Повторно привязать тот же ключ всё равно не выйдет: сервер отдаёт
        список уже привязанных, и браузер отказывает сам.
      */}
      <p className="set__hint">Ключ живёт в устройстве, а не в учётной записи.</p>

      {notice !== undefined && <p className="set__hint">{notice}</p>}
    </section>
  );
}

/**
 * Устройства и отключение (3.16.5).
 *
 * Отключение действует сразу и на сервере: сессии гаснут, а открытый сокет
 * закрывается. Своё устройство в списке отмечено и отключается тоже — но
 * подписью «Выйти здесь», потому что это ровно то, что произойдёт.
 */
function DeviceSection({
  session,
  onSignedOut,
  onFlushBeforeSignOut,
}: {
  session: Pick<Session, 'listDevices' | 'revokeDevice'>;
  onSignedOut: () => void;
  onFlushBeforeSignOut: () => Promise<number>;
}): React.ReactElement {
  const [devices, setDevices] = useState<DeviceSummary[] | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [showGone, setShowGone] = useState(false);

  const refresh = useCallback(() => {
    session.listDevices().then(setDevices, () => setDevices(undefined));
  }, [session]);

  useEffect(refresh, [refresh]);

  const disconnect = useCallback(
    async (device: DeviceSummary): Promise<void> => {
      try {
        // Выход на этом устройстве стирает локальную копию вместе с очередью,
        // а сессию гасит раньше — то есть после отзыва досылать уже и нечем, и
        // некуда. Поэтому сначала досылаем, и если что-то осталось, не
        // выходим вовсе: уничтожить молча правки, о которых подвал только что
        // говорил «ждут», — это ровно то враньё, против которого заведён
        // индикатор.
        if (device.current) {
          const stuck = await onFlushBeforeSignOut();
          if (stuck > 0) {
            setNotice(
              `Правки ещё не уехали — ждут ${String(stuck)}. Выйти можно, когда в подвале будет «сохранено».`,
            );
            return;
          }
        }

        await session.revokeDevice(device.id);
      } catch {
        setNotice('Отключить не вышло — попробуйте ещё раз');
        return;
      }

      // Выход здесь же не оставляют висеть до первой неудачной правки:
      // приложение уже не имеет входа, и локальную копию надо отдать экрану
      // входа сразу.
      if (device.current) {
        onSignedOut();
        return;
      }

      setNotice('Устройство отключено');
      refresh();
    },
    [session, onSignedOut, onFlushBeforeSignOut, refresh],
  );

  // Отключённые входы — история, а не список входов: держать их на виду
  // значит отвечать не на тот вопрос, с которым сюда приходят («откуда я
  // сейчас вошёл»). Но и стирать их нельзя: по ним замечают чужой вход.
  const active = devices?.filter((device) => device.active) ?? [];
  const gone = devices?.filter((device) => !device.active) ?? [];
  const shown = showGone ? [...active, ...gone] : active;

  return (
    <section className="set">
      <h2 className="set__title">Устройства</h2>

      {devices === undefined ? (
        <p className="set__hint">Список устройств не пришёл — сервер не ответил.</p>
      ) : (
        <ul className="links__list" aria-label="Устройства">
          {shown.map((device) => (
            <li key={device.id} className="links__item">
              <span className="set__row">{deviceLabel(device)}</span>
              {device.active && (
                <button
                  type="button"
                  className="danger"
                  aria-label={
                    device.current
                      ? 'Выйти на этом устройстве'
                      : `Отключить устройство: ${deviceLabel(device)}`
                  }
                  onClick={() => {
                    void disconnect(device);
                  }}
                >
                  {device.current ? 'Выйти здесь' : 'Отключить'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {gone.length > 0 && (
        <button type="button" className="link" onClick={() => setShowGone(!showGone)}>
          {showGone ? 'Скрыть отключённые' : `Показать отключённые (${String(gone.length)})`}
        </button>
      )}

      {devices !== undefined && devices.length > 0 && (
        <Details summary="Чем «Отключить» отличается от «Выйти здесь»">
          Действие одно — сервер гасит все входы устройства. Разница в том, чей доступ прекращается:
          «Отключить» — другого устройства, вы продолжаете работать; «Выйти здесь» — этого, и вы
          попадаете на экран входа. И то и другое срабатывает сразу, не дожидаясь конца сессии.
        </Details>
      )}
      {notice !== undefined && <p className="set__hint">{notice}</p>}
    </section>
  );
}

/**
 * Как хранятся данные — своими словами и без обещаний, которых продукт не даёт
 * (3.18).
 *
 * Сказано именно то, что есть сейчас: содержимое зашифровано, но ключ у
 * сервера, то есть владелец сервера технически может прочитать задачи. Режим,
 * где ключ только у пользователя, — этап 18, и до него обещать его нельзя.
 */
function SecuritySection({
  session,
  email,
  onDeleted,
}: {
  session: Pick<Session, 'deleteAccount'>;
  email: string | undefined;
  onDeleted: () => void;
}): React.ReactElement {
  return (
    <section className="set">
      <h2 className="set__title">Безопасность</h2>
      <p className="set__hint">
        Задачи хранятся зашифрованными, но ключ пока у сервера, а не у вас.
      </p>

      <Details summary="Что это значит">
        Украденная копия базы или бэкапа не раскроет, что написано в задачах: содержимое
        зашифровано, а ключ лежит отдельно. Но ключ у сервера — значит, тот, кто его держит,
        технически может задачи прочитать. Режим, в котором ключ есть только у вас, задуман, но ещё
        не сделан, и обещать его до этого мы не будем. И отдельно: скрывается содержимое, но не факт
        существования данных — объём и ритм правок со стороны сервера видны.
      </Details>

      <DeleteAccount session={session} email={email} onDeleted={onDeleted} />
    </section>
  );
}

/**
 * Удаление учётной записи вместе с данными (3.18.7).
 *
 * Подтверждение здесь не двумя нажатиями, как у задачи, а набранным адресом:
 * два нажатия защищают от промаха, а тут промахнуться нельзя вовсе — корзины
 * для учётной записи не существует. Набранный адрес превращает случайность в
 * осознанное действие, и он же не даёт удалить не ту запись, если человек
 * забыл, под кем вошёл на этом устройстве.
 */
function DeleteAccount({
  session,
  email,
  onDeleted,
}: {
  session: Pick<Session, 'deleteAccount'>;
  email: string | undefined;
  onDeleted: () => void;
}): React.ReactElement {
  const [asking, setAsking] = useState(false);
  const [typed, setTyped] = useState('');
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const matches = email !== undefined && typed.trim().toLowerCase() === email.toLowerCase();

  if (!asking) {
    return (
      <button type="button" className="danger" onClick={() => setAsking(true)}>
        Удалить учётную запись
      </button>
    );
  }

  return (
    <div className="set__danger">
      {/*
        Про «заберите данные файлом» здесь не сказано намеренно: выгрузки
        (3.13.5) ещё нет, и совет, которому нельзя последовать, — это ложь на
        самом неудачном экране. Строку сюда добавит тот, кто сделает экспорт.
      */}
      <p className="set__hint">
        Удалится всё: задачи, проекты, теги, чек-листы и удалённые задачи. Это не обычное удаление —
        вернуть будет нечего и неоткуда.
      </p>

      <label className="set__hint" htmlFor="delete-account-email">
        Наберите {email ?? 'адрес учётной записи'}, чтобы подтвердить:
      </label>
      <input
        id="delete-account-email"
        type="email"
        autoComplete="off"
        value={typed}
        aria-label="Адрес учётной записи для подтверждения"
        onChange={(event) => setTyped(event.target.value)}
      />

      <div className="set__row-actions">
        <button
          type="button"
          className="danger"
          // `aria-disabled`, а не `disabled`: кнопка остаётся в обходе с
          // клавиатуры и объясняет, чего не хватает, вместо того чтобы молча
          // не нажиматься.
          aria-disabled={!matches}
          onClick={() => {
            if (!matches || email === undefined) {
              setNotice('Адрес не совпадает с адресом учётной записи');
              return;
            }

            setNotice('Удаляем…');
            session.deleteAccount(email).then(onDeleted, () => {
              setNotice('Удалить не вышло — попробуйте ещё раз');
            });
          }}
        >
          Удалить навсегда
        </button>
        <button
          type="button"
          onClick={() => {
            setAsking(false);
            setTyped('');
            setNotice(undefined);
          }}
        >
          Отмена
        </button>
      </div>

      {notice !== undefined && <p className="set__hint">{notice}</p>}
    </div>
  );
}

/**
 * Отказ сервера человеческими словами.
 *
 * Голый `message` — это английский текст WebKit или код вроде
 * `last_passkey`: правда, из которой ничего не следует.
 */
function describeKeyFailure(error: unknown): string {
  const code = (error as { code?: string }).code;

  if (code === 'passkey_last_method') {
    return 'Это единственный способ входа, а почта ещё не подтверждена: снимете ключ — войти будет нечем';
  }
  if (code === 'passkey_not_found') return 'Такого ключа уже нет';

  return 'Не вышло — попробуйте ещё раз';
}
